// Web-app-only field shown to the right of the Order Type ("New Build")
// dropdown. When filled, Save & Submit creates a Customer Part Number record
// (customrecord_scm_customerpartnumber) for the item — see SaveSubmitButton.
// Its value is held in AnswersContext, deliberately outside `answers`, so it
// never feeds the CPQ operations or the generated ID key.
//
// IN-USE STATUS: a part number is unique — there is one record per number
// (INCLUDE NAME FIELD is on). So as the user types, we check who holds it and
// show the result under the field:
//
//   Available                 — nothing holds it; this configuration will claim it
//   Already on this item      — the record we'd create/reuse is this item's own
//   In use                    — another item holds it: the item, its customer, and
//                               who created that record + when
//
// It NEVER blocks. Taking an old customer part number over and giving it to this
// part is a normal thing to do; what's usually unknown is whether the number is
// in use at all. So this answers that question and names the consequence — the
// existing record is renamed "<number>_N", the claim-and-rename rule the POST
// endpoint implements — and the user decides: carry on, or type a different
// number. Save & Submit never consults this status.
//
// The lookup is read-only, debounced, and best-effort: a failure shows nothing
// rather than an error, and never affects saving.

import { useEffect, useRef, useState } from "react";
import { useAnswers } from "../state/AnswersContext";
import { checkCustomerPartNumber, type CpnCheck } from "../util/netsuiteClient";

type Status =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "free" }
  | { kind: "mine"; holderId: string }
  | { kind: "taken"; check: CpnCheck }
  // The lookup didn't complete. Previously this fell back to `idle`, which
  // renders NOTHING — so the user watched "Checking availability…" disappear
  // with no answer and no way to tell whether the number was free. Say it
  // plainly instead; saving is unaffected either way.
  | { kind: "unknown" };

// "7/29/2026" from whatever NetSuite returns; blank/unparseable passes through.
function shortDate(v: string | null): string {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
}

export default function CustomerPartNumberField() {
  const { customerPartNumber, setCustomerPartNumber, editItemId } = useAnswers();
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  // Guards against a slow earlier response overwriting a newer one.
  const seq = useRef(0);

  const value = customerPartNumber.trim();

  useEffect(() => {
    if (!value) {
      setStatus({ kind: "idle" });
      return;
    }
    const mine = ++seq.current;
    setStatus({ kind: "checking" });
    const t = window.setTimeout(() => {
      checkCustomerPartNumber(value, { detail: true }).then((check) => {
        if (mine !== seq.current) return; // superseded by a newer keystroke
        if (!check || check.ok === false) {
          setStatus({ kind: "unknown" }); // couldn't check — never leave it blank
          return;
        }
        if (!check.exists || check.holders.length === 0) {
          setStatus({ kind: "free" });
          return;
        }
        // On Edit Config the field is pre-filled with the launched item's own
        // number — that's not a conflict, so call it out separately.
        const own =
          editItemId &&
          check.holders.find((h) => h.itemId && String(h.itemId) === String(editItemId));
        setStatus(own ? { kind: "mine", holderId: own.id } : { kind: "taken", check });
      });
    }, 500);
    return () => window.clearTimeout(t);
  }, [value, editItemId]);

  return (
    <div
      className="cpq-question"
      data-code="CUSTOMER_PART_NUMBER"
      style={{ gridColumn: "span 4" }}
    >
      <label className="cpq-question-label">Customer Part Number</label>
      <input
        type="text"
        className="cpq-input"
        value={customerPartNumber}
        onChange={(e) => setCustomerPartNumber(e.target.value)}
      />
      <CpnStatus status={status} />
    </div>
  );
}

function CpnStatus({ status }: { status: Status }) {
  if (status.kind === "idle") return null;

  if (status.kind === "checking") {
    return <div className="cpq-cpn-status is-checking">Checking availability…</div>;
  }
  if (status.kind === "unknown") {
    return (
      <div className="cpq-cpn-status is-unknown">
        <strong>Couldn’t check</strong> — the part-number lookup didn’t respond. You can
        still save; if the number is already in use it will be taken over as usual.
      </div>
    );
  }
  if (status.kind === "free") {
    return (
      <div className="cpq-cpn-status is-free">
        <strong>Available</strong> — not in use.
      </div>
    );
  }
  if (status.kind === "mine") {
    return (
      <div className="cpq-cpn-status is-mine">
        <strong>Already on this item</strong> — this configuration holds it.
      </div>
    );
  }

  // Taken by one or more other items. INFORMATION, NOT A GATE: taking an old
  // customer part number over and giving it to this part is a normal thing to
  // do — what's usually unknown is whether the number is in use at all. So the
  // status answers that and names the consequence, and the user decides: carry
  // on, or type a different number. Nothing here blocks Save & Submit (which
  // never consults it), hence no role="alert" — this isn't an error.
  const { holders, nextFreeName, detail } = status.check;
  return (
    <div className="cpq-cpn-status is-taken">
      <strong>In use</strong> — you can still take it over, or enter a different number.
      <ul className="cpq-cpn-holders">
        {holders.map((h) => (
          <li key={h.id}>
            <span className="cpq-cpn-item">{h.itemName || `item ${h.itemId ?? h.id}`}</span>
            {h.customerName ? <> · {h.customerName}</> : h.customerId ? <> · customer {h.customerId}</> : null}
            {h.createdBy || h.createdOn ? (
              <div className="cpq-cpn-meta">
                created by {h.createdBy || "unknown"}
                {h.createdOn ? ` · ${shortDate(h.createdOn)}` : ""}
              </div>
            ) : detail === false ? (
              <div className="cpq-cpn-meta">creator unavailable for this record type</div>
            ) : null}
          </li>
        ))}
      </ul>
      <div className="cpq-cpn-meta">
        Saving will rewrite that customer part number to <code>{nextFreeName}</code>.
      </div>
    </div>
  );
}
